import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CloudinaryUploadResponse } from '@/services/cloudinary';
import ImageUpload from '@/components/ImageUpload';
import { Save, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ArticleDetailsEditProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  originalLink?: string;
  imageData: CloudinaryUploadResponse | null;
  onTitleChange: (title: string) => void;
  onDescriptionChange: (description: string) => void;
  onOriginalLinkChange: (originalLink: string) => void;
  onImageDataChange: (imageData: CloudinaryUploadResponse | null) => void;
  onContinueToSentences: () => void;
  isEditingExistingArticle?: boolean;
}

const ArticleDetailsEdit = ({
  open,
  onOpenChange,
  title,
  description,
  originalLink,
  imageData,
  onTitleChange,
  onDescriptionChange,
  onOriginalLinkChange,
  onImageDataChange,
  onContinueToSentences,
  isEditingExistingArticle = false,
}: ArticleDetailsEditProps) => {
  const { t } = useTranslation();
  const [localTitle, setLocalTitle] = useState(title);
  const [localDescription, setLocalDescription] = useState(description);
  const [localOriginalLink, setLocalOriginalLink] = useState(
    originalLink || ''
  );

  // Sync local state with props when dialog opens or props change
  useEffect(() => {
    if (open) {
      setLocalTitle(title);
      setLocalDescription(description);
      setLocalOriginalLink(originalLink || '');
    }
  }, [open, title, description, originalLink]);

  const handleSaveAndContinue = () => {
    onTitleChange(localTitle);
    onDescriptionChange(localDescription);
    onOriginalLinkChange(localOriginalLink);
    onContinueToSentences();
  };

  const handleImageUpload = (uploadedImageData: CloudinaryUploadResponse) => {
    onImageDataChange(uploadedImageData);
  };

  const handleImageRemove = () => {
    onImageDataChange(null);
  };

  const isFormValid = localTitle.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditingExistingArticle
              ? t('articleDetailsEdit.editArticleDetails')
              : t('articleDetailsEdit.articleDetails')}
          </DialogTitle>
          <DialogDescription>
            {isEditingExistingArticle
              ? t('articleDetailsEdit.editDescription')
              : t('articleDetailsEdit.setupDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="edit-title">
              {t('articleDetailsEdit.articleTitle')}
            </Label>
            <Input
              id="edit-title"
              value={localTitle}
              onChange={e => setLocalTitle(e.target.value)}
              placeholder={t('placeholders.enterArticleTitle')}
              className="bg-white/50"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="edit-description">
              {t('articleDetailsEdit.description')}
            </Label>
            <Input
              id="edit-description"
              value={localDescription}
              onChange={e => setLocalDescription(e.target.value)}
              placeholder={t('placeholders.enterArticleDescription')}
              className="bg-white/50"
            />
          </div>

          {/* Original Link */}
          <div className="space-y-2">
            <Label htmlFor="edit-original-link">
              {t('articleDetailsEdit.originalLink')}
            </Label>
            <Input
              id="edit-original-link"
              type="url"
              value={localOriginalLink}
              onChange={e => setLocalOriginalLink(e.target.value)}
              placeholder={t('articleDetailsEdit.originalLinkPlaceholder')}
              className="bg-white/50"
            />
            <p className="text-sm text-muted-foreground">
              {t('articleDetailsEdit.originalLinkDescription')}
            </p>
          </div>

          {/* Cover Image */}
          <div className="space-y-2">
            <Label>{t('articleDetailsEdit.coverImage')}</Label>
            <ImageUpload
              onImageUpload={handleImageUpload}
              onImageRemove={handleImageRemove}
              currentImageUrl={imageData?.secure_url}
              className="space-y-2"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('actions.cancel')}
          </Button>
          <Button
            onClick={handleSaveAndContinue}
            disabled={!isFormValid}
            className="bg-gradient-hero hover:opacity-90"
          >
            {isEditingExistingArticle ? (
              <>
                <Save className="h-4 w-4 mr-2" />
                {t('articleDetailsEdit.continueToSentences')}
              </>
            ) : (
              <>
                <ArrowRight className="h-4 w-4 mr-2" />
                {t('articleDetailsEdit.continueToSentences')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ArticleDetailsEdit;
